import { Box } from "@mui/material";
import { ValidatedTextField } from "components/common/inputs/validatedTextField";
import { useTenantTranslation } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { Serialiser } from "hooks/serialisers/types";
import {
  MaterialLimit,
  MaterialLimitBounds,
  ParameterConflict,
} from "src/store/api/generatedApi";
import { MarkerDot } from "./MarkerDot";
import { ConflictMarkerDot } from "./ConflictMarkerDot";
import { InequalityLayout, slotInputSx } from "./MaterialLimitInequality";
import {
  boundForScope,
  MaterialLimitBoundField,
  MaterialLimitScope,
} from "./materialLimitMatrix";

interface Props {
  limits: MaterialLimit[];
  childCount: number;
  scope: MaterialLimitScope;
  hardnessView: boolean;
  activeFields: MaterialLimitBoundField[];
  boundSerialiser: Serialiser<number | null, never>;
  anyChildDiffersFromScrapchef: boolean;
  conflicts: ParameterConflict[];
  onChange: (
    field: MaterialLimitBoundField | "hardness",
    value: number | null
  ) => void;
}

/**
 * The editable summary on a product-group row. A slot shows a concrete value
 * only when every chef group in the group shares it; if any lack it or differ
 * it shows blank with a "~" hint, since editing writes to all of them.
 */
export const MaterialLimitAggregateCell = ({
  limits,
  childCount,
  scope,
  hardnessView,
  activeFields,
  boundSerialiser,
  anyChildDiffersFromScrapchef,
  conflicts,
  onChange,
}: Props) => {
  const { t } = useTenantTranslation();
  const serialiser = useNumberSerialiser({
    min: 0,
    default: { value: null, text: "" },
    decimalPlaces: 3,
  });

  if (limits.length === 0) {
    return null;
  }

  const slot = (
    setValues: (number | null)[],
    field: MaterialLimitBoundField | "hardness"
  ) => {
    const missing = Math.max(childCount - setValues.length, 0);
    const distinct = new Set([
      ...setValues,
      ...new Array<null>(missing).fill(null),
    ]);
    const shared = distinct.size === 1 ? ([...distinct][0] ?? null) : null;
    const varies = distinct.size > 1;
    return (
      <ValidatedTextField
        value={shared}
        setValue={(next) => {
          onChange(field, next);
        }}
        serialiser={field === "hardness" ? serialiser : boundSerialiser}
        textFieldProps={{
          variant: "standard",
          placeholder: varies ? t("varies") : undefined,
          sx: slotInputSx,
        }}
      />
    );
  };

  const marker = anyChildDiffersFromScrapchef ? (
    <MarkerDot
      corner="top-left"
      color="secondary.main"
      tooltip={t("someGroupsDifferFromScrapchef")}
    />
  ) : null;

  const conflictMarker =
    conflicts.length > 0 ? (
      <ConflictMarkerDot
        conflicts={conflicts}
        corner="bottom-left"
        tooltip={t("someGroupsHaveConflicts")}
      />
    ) : null;

  const markers = (
    <>
      {marker}
      {conflictMarker}
    </>
  );

  const outerSx = {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as const;

  if (hardnessView) {
    return (
      <Box
        onClick={(event) => {
          event.stopPropagation();
        }}
        sx={outerSx}
      >
        {markers}
        <Box
          sx={{
            border: 1,
            borderColor: "primary.main",
            borderRadius: 1,
            px: 0.5,
            display: "inline-flex",
          }}
        >
          {slot(
            limits.map((limit) => limit.hardness),
            "hardness"
          )}
        </Box>
      </Box>
    );
  }

  const bounds = limits
    .map((limit) => boundForScope(limit, scope))
    .filter((bound): bound is MaterialLimitBounds => bound !== undefined);
  if (bounds.length === 0) {
    return marker === null && conflictMarker === null ? null : (
      <Box sx={outerSx}>{markers}</Box>
    );
  }

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
      }}
      sx={outerSx}
    >
      {markers}
      <InequalityLayout
        fields={activeFields}
        renderSlot={(field) =>
          slot(
            bounds.map((bound) => bound[field]),
            field
          )
        }
      />
    </Box>
  );
};
